import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, Spin, notification } from 'antd';
import { CheckOutlined, DownOutlined, BankOutlined } from '@ant-design/icons';
import {
  OrgSwitcherWrapper,
  OrgSwitcherLabel,
  OrgSwitcherButton,
  OrgSwitcherName,
  OrgSwitcherMenu,
  OrgSwitcherMenuItem,
  OrgSwitcherEmpty,
  OrgSwitcherCollapsed,
} from './OrgSwitcherStyle';
import {
  getMyOrganizations,
  switchOrganization,
  persistSwitchedSession,
} from '../../services/multiOrg.service';
import { STORAGE_KEYS, getUserData, isMultiOrgUser } from '../../utils/permissions';
import { landOnToplineOr } from '../../store/actions/projectAction';

// Only users the server has flagged see any of this. isMultiOrgUser reads the
// flag from the login payload, so an absent flag keeps the feature completely
// invisible and the chrome identical to a single-org user's.

// A page mounts more than one switcher (sidebar plus the mobile header bar and
// drawer). The cross-tab reload must fire once, not once per instance.
let crossTabReloadFired = false;

const initials = (name) => {
  if (!name) return 'OR';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const OrgSwitcher = ({ collapsed = false, inline = false, onSwitchStart, onNavigate }) => {
  const enabled = useMemo(() => isMultiOrgUser(), []);
  const userData = useMemo(() => getUserData(), []);

  const [open, setOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(
    () => userData?.organization?._id || userData?.organization_id || null
  );
  const wrapperRef = useRef(null);

  const activeOrgName =
    organizations.find((org) => String(org._id) === String(activeOrgId))?.name ||
    userData?.organization?.name ||
    'Organization';

  const loadOrganizations = useCallback(async () => {
    setLoadingList(true);
    const result = await getMyOrganizations();
    setLoadingList(false);
    if (!result.success || !result.data) {
      // A 403 already surfaced through the shared axios interceptor. Anything
      // else is a load failure the user can retry by reopening the list.
      if (result.status !== 403) {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to load your organizations',
        });
      }
      return;
    }
    setOrganizations(result.data.organizations || []);
    if (result.data.active_organization_id) {
      setActiveOrgId(String(result.data.active_organization_id));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    loadOrganizations();
  }, [enabled, loadOrganizations]);

  useEffect(() => {
    if (!enabled || !open) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [enabled, open]);

  const handleSelect = async (organizationId) => {
    if (switching) return;
    if (String(organizationId) === String(activeOrgId)) {
      setOpen(false);
      return;
    }

    setSwitching(true);
    if (onSwitchStart) onSwitchStart();
    const result = await switchOrganization(organizationId);

    if (!result.success || !result.data) {
      setSwitching(false);
      if (result.status !== 403) {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to switch organization',
        });
      }
      return;
    }

    // The cached login payload is what paints the org logo, name and the whole
    // permission set. Rewriting both keys before the reload is mandatory,
    // otherwise the previous organization's branding survives the switch.
    // Storage failure here cannot be reconciled: the server has already moved
    // the session while the cache still describes the old org, so the helper
    // ends the session rather than leaving the two disagreeing.
    if (!persistSwitchedSession(result.data)) return;

    // The switch response may carry the new org's landing chat, mirroring the
    // login flow. Absent or empty is the normal case and must not break here.
    // As at login, the new org's Topline comes first when it holds a summary
    // this person can see (client 2026-09-28).
    const redirectChatId = result.data.redirect_chat_id;
    landOnToplineOr(() => { window.location.href = '/topline'; }, () => {
      if (redirectChatId) {
        window.location.href = `/insight-engine/${redirectChatId}`;
        return;
      }

      if (onNavigate) onNavigate();
      window.location.reload();
    });
  };

  // Another tab switched organizations. The cached payload underneath this tab
  // is now the wrong org, so reload rather than keep writing into a stale one.
  useEffect(() => {
    if (!enabled) return undefined;
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEYS.USER_DATA || !event.newValue) return;
      let nextOrgId = null;
      try {
        const next = JSON.parse(event.newValue);
        nextOrgId = next?.organization?._id || next?.organization_id || null;
      } catch (e) {
        return;
      }
      if (!nextOrgId) return;
      if (String(nextOrgId) === String(activeOrgId)) return;
      if (crossTabReloadFired) return;
      crossTabReloadFired = true;
      window.location.reload();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [enabled, activeOrgId]);

  if (!enabled) return null;

  if (collapsed) {
    return (
      <Tooltip
        title={`Active organization: ${activeOrgName}`}
        placement={inline ? 'bottom' : 'right'}
      >
        <OrgSwitcherCollapsed $inline={inline}>{initials(activeOrgName)}</OrgSwitcherCollapsed>
      </Tooltip>
    );
  }

  const hasChoices = organizations.length > 1;

  return (
    <OrgSwitcherWrapper ref={wrapperRef}>
      <OrgSwitcherLabel>Active organization</OrgSwitcherLabel>
      <OrgSwitcherButton
        $interactive={hasChoices && !switching}
        onClick={() => {
          if (!hasChoices || switching) return;
          setOpen((prev) => !prev);
        }}
      >
        <BankOutlined style={{ fontSize: 14, color: '#6D6A67' }} />
        <OrgSwitcherName title={activeOrgName}>{activeOrgName}</OrgSwitcherName>
        {switching ? (
          <Spin size="small" />
        ) : (
          hasChoices && (
            <DownOutlined
              style={{
                fontSize: 10,
                color: '#6D6A67',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )
        )}
      </OrgSwitcherButton>

      {open && (
        <OrgSwitcherMenu>
          {loadingList && organizations.length === 0 ? (
            <OrgSwitcherEmpty>Loading organizations...</OrgSwitcherEmpty>
          ) : organizations.length === 0 ? (
            <OrgSwitcherEmpty>No organizations available</OrgSwitcherEmpty>
          ) : (
            organizations.map((org) => {
              const isActive = String(org._id) === String(activeOrgId);
              return (
                <OrgSwitcherMenuItem
                  key={org._id}
                  $active={isActive}
                  onClick={() => handleSelect(org._id)}
                >
                  <span className="orgName" title={org.name}>
                    {org.name}
                  </span>
                  {isActive && <CheckOutlined style={{ fontSize: 11 }} />}
                </OrgSwitcherMenuItem>
              );
            })
          )}
        </OrgSwitcherMenu>
      )}
    </OrgSwitcherWrapper>
  );
};

export default OrgSwitcher;
